import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Modal } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import RadioButton from 'components/inputs/RadioButton';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { UPDATE_STATUS_CODE } from 'components/common/constant';
import { GET_FLEET_RESERVATION, RESCHEDULE_FLEET_RESERVATION, CANCEL_FLEET_RESERVATION } from 'actions/fleetReservation';
import {
  StatusPill,
  canManageFleetReservations,
  SCOPES,
  REPEAT_TYPES,
  BUFFER_MINUTES,
  HISTORY_META,
  formatDate,
  formatDateTime,
  formatTime,
  formatWindow,
  describeRepeat,
  blockStartOf,
  editableDates,
  buildScopePayload,
} from './fleetReservationUtils';

const LEVEL = { STATION: 'Entire station', CHARGER: 'Single charger', CONNECTOR: 'Connector' };
const emptyForm = { scope: 'THIS_OCCURRENCE', occurrenceDate: '', newDate: '', timeFrom: '', timeTo: '', reason: '' };

const Field = ({ label, children, className = '' }) => (
  <div className="fr-value">
    <div className="fr-label">{label}</div>
    <div className={className}>{children || '—'}</div>
  </div>
);

Field.propTypes = { label: PropTypes.string, children: PropTypes.node, className: PropTypes.string };

const historyText = (entry) => {
  const previous = entry.previousData || {};
  const next = entry.newData || {};
  if (entry.action === 'CREATED') {
    const window = formatWindow(next.bookedStartTime, next.bookedEndTime);
    return [next.repeatType, window, next.occurrenceCount && `${next.occurrenceCount} occurrence${next.occurrenceCount === 1 ? '' : 's'}`]
      .filter(Boolean)
      .join(' · ');
  }
  const parts = [];
  const before = formatWindow(previous.bookedStartTime, previous.bookedEndTime);
  const after = formatWindow(next.timeFrom || next.bookedStartTime, next.timeTo || next.bookedEndTime);
  if (after) parts.push(before ? `${before} changed to ${after}` : `New window ${after}`);
  if (next.newDate) parts.push(`moved ${previous.reservationDate || ''} → ${next.newDate}`.trim());
  if (next.updatedRowsCount) parts.push(`${next.updatedRowsCount} row${next.updatedRowsCount === 1 ? '' : 's'} updated`);
  return parts.join(' · ');
};

const FleetReservationDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reservationCode } = useParams();
  const location = useLocation();
  const rowActionHandled = useRef(false);
  const { navigateTo } = useContext(NavContext);
  const { detail, isDetailLoading, isSaving } = useSelector((state) => state.fleetReservation);
  const rolePermissionData = useSelector((state) => state.manageRole.manageRoles);
  const profileData = useSelector((state) => state.profile.userProfile);
  const canManage = canManageFleetReservations(profileData, rolePermissionData);

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);

  const rescheduleDates = useMemo(() => (detail ? editableDates(detail, 'reschedule') : []), [detail]);
  const cancelDates = useMemo(() => (detail ? editableDates(detail, 'cancel') : []), [detail]);
  const isShowingOther = !detail || detail.reservationCode !== reservationCode;
  const isCancel = modal === 'cancel';
  const dates = isCancel ? cancelDates : rescheduleDates;
  const needsDate = form.scope !== 'ENTIRE_RESERVATION';
  const formErrors = {
    occurrenceDate:
      needsDate && !form.occurrenceDate
        ? 'Select the occurrence date'
        : needsDate && !dates.includes(form.occurrenceDate)
          ? `No ${isCancel ? 'active' : 'editable future'} occurrence on this date`
          : '',
    time: !isCancel && (!form.timeFrom || !form.timeTo || form.timeTo <= form.timeFrom) ? 'End time must be after start time' : '',
    reason: !form.reason.trim() ? (isCancel ? 'Reason for cancellation is required' : 'Reason for change is required') : '',
  };

  const load = () => dispatch({ type: GET_FLEET_RESERVATION.REQUEST, payload: reservationCode });

  useEffect(() => {
    load();
  }, [reservationCode]);

  const openModal = (type, occurrenceDate) => {
    const typeDates = type === 'cancel' ? cancelDates : rescheduleDates;
    setForm({
      ...emptyForm,
      occurrenceDate: typeDates.includes(occurrenceDate) ? occurrenceDate : typeDates[0] || '',
      timeFrom: formatTime(detail.bookedStartTime),
      timeTo: formatTime(detail.bookedEndTime),
    });
    setSubmitted(false);
    setModal(type);
  };

  useEffect(() => {
    const action = _.get(location, 'state.action');
    if (isShowingOther || isDetailLoading || rowActionHandled.current || !['reschedule', 'cancel'].includes(action)) return;
    rowActionHandled.current = true;
    openModal(action, _.get(location, 'state.occurrenceDate'));
  }, [detail, isDetailLoading]);

  const setFormField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setScope = (scope) =>
    setForm((prev) => ({
      ...prev,
      scope,
      occurrenceDate: scope === 'ENTIRE_RESERVATION' ? '' : prev.occurrenceDate || dates[0] || '',
      newDate: scope === 'THIS_OCCURRENCE' ? prev.newDate : '',
    }));
  const effectiveBlock = form.timeFrom && form.timeTo ? formatWindow(blockStartOf(form.timeFrom), form.timeTo) : '';

  const handleSubmit = () => {
    setSubmitted(true);
    if (_.some(formErrors)) return;
    const reason = form.reason.trim();
    const payload = isCancel
      ? { ...buildScopePayload(form), cancelReason: reason }
      : {
          ...buildScopePayload(form),
          updates: {
            ...(form.scope === 'THIS_OCCURRENCE' && form.newDate && { newDate: form.newDate }),
            timeFrom: form.timeFrom,
            timeTo: form.timeTo,
          },
          reason,
        };
    dispatch({
      type: isCancel ? CANCEL_FLEET_RESERVATION.REQUEST : RESCHEDULE_FLEET_RESERVATION.REQUEST,
      payload: { reservationCode, ...payload },
      cb: (res) => {
        if (_.get(res, 'status') !== UPDATE_STATUS_CODE) return;
        toast.success(isCancel ? 'Reservation cancelled' : 'Reservation rescheduled');
        setModal(null);
        const nextCode = _.get(res, 'data.reservationCode');
        if (nextCode && nextCode !== reservationCode) navigateTo(`/fleet-reservations/${nextCode}`);
        else load();
      },
    });
  };

  const renderBody = () => {
    if (isDetailLoading && isShowingOther) return <Skeleton height={200} />;
    if (isShowingOther) return <div className="empty-data-block">Reservation not found</div>;

    const { charging, occurrences } = detail;

    return (
      <>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <h4 className="mb-0 fr-code">{detail.reservationCode}</h4>
            <StatusPill status={detail.status} />
            {detail.isActive && <span className="fr-active-dot">Active</span>}
          </div>
          {canManage && (
            <div className="d-flex flex-wrap gap-2">
              <button type="button" className="btn fr-btn-outline" disabled={!rescheduleDates.length} onClick={() => openModal('reschedule')}>
                Reschedule
              </button>
              <button type="button" className="btn fr-btn-danger" disabled={!cancelDates.length} onClick={() => openModal('cancel')}>
                Cancel Reservation
              </button>
            </div>
          )}
        </div>

        <div className="fr-detail-cards">
          <div className="fr-detail-card">
            <h6>General info</h6>
            <Field label="Reservation code">
              <span className="fr-code">{detail.reservationCode}</span>
            </Field>
            <Field label="Status">
              <StatusPill status={detail.status} />
            </Field>
            <Field label="Fleet owner">{detail.fleetName}</Field>
            <Field label="Driver">{detail.memberName}</Field>
            <Field label="Created by">{detail.createdBy}</Field>
            <Field label="Created at">{formatDateTime(detail.createdAt)}</Field>
          </div>
          <div className="fr-detail-card">
            <h6>Resource</h6>
            <Field label="Station">{detail.stationName}</Field>
            <Field label="Target type">{LEVEL[detail.targetType] || detail.targetType}</Field>
            <Field label="Charger ID">
              {detail.resources.length > 0 && _.uniq(detail.resources.map((resource) => resource.chargerOcppId)).join(', ')}
            </Field>
            <Field label="Connector">
              {detail.resources.length > 0 &&
                detail.resources.map((resource) => (
                  <div key={`${resource.chargerId}-${resource.connectorId}`}>
                    {_.isNil(resource.connectorId)
                      ? `${resource.chargerOcppId}: all connectors`
                      : `${resource.chargerOcppId}: connector ${resource.connectorId}`}
                  </div>
                ))}
            </Field>
          </div>
          <div className="fr-detail-card">
            <h6>Schedule</h6>
            <Field label="Repeat type">
              {_.get(_.find(REPEAT_TYPES, { value: detail.repeatType }), 'label', detail.repeatType)}
              {detail.repeatType && detail.repeatType !== 'ONCE' && (
                <div className="fr-muted small">
                  {describeRepeat({
                    repeatType: detail.repeatType,
                    startDate: detail.seriesStartDate,
                    endDate: detail.seriesEndDate,
                    daysOfWeek: detail.daysOfWeek,
                  })}
                </div>
              )}
            </Field>
            <Field label="Date span">
              {detail.seriesStartDate && `${formatDate(detail.seriesStartDate, 'YYYY-MM-DD')} - ${formatDate(detail.seriesEndDate, 'YYYY-MM-DD')}`}
            </Field>
            <Field label="Booked window">{formatWindow(detail.bookedStartTime, detail.bookedEndTime)}</Field>
            <Field label="Buffer">
              {detail.bufferMinutes && (
                <>
                  {detail.bufferMinutes} minutes · blocked{' '}
                  <span className="fr-blocked">{formatWindow(detail.blockStartTime, detail.bookedEndTime)}</span>
                </>
              )}
            </Field>
            <Field label="Total occurrences">{detail.totalOccurrences}</Field>
          </div>
          <div className="fr-detail-card">
            <h6>Charging</h6>
            <Field label="Actual start">{formatDateTime(charging.actualStartTime)}</Field>
            <Field label="Actual end">{formatDateTime(charging.actualEndTime)}</Field>
            <Field label="Session ID">{charging.sessionId}</Field>
            <Field label="Hand-over" className="fr-blocked">
              {charging.handover && `B2C session ${charging.handover.sessionId} stopped ${formatTime(charging.handover.stoppedAt)}`}
            </Field>
          </div>
        </div>

        <Card>
          <div className="mb-2">
            <b>Occurrences</b> <span className="fr-muted small">{occurrences.length} total · one row per charger per date</span>
          </div>
          <div className="table-responsive">
            <table className="record-list-table">
              <thead>
                <tr>
                  {['Date', 'Charger ID', 'Connector', 'Time Window', 'Blocked from', 'Status', 'Actual'].map((heading) => (
                    <th key={heading}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {occurrences.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="border-0">
                      <div className="empty-data-block">No occurrences</div>
                    </td>
                  </tr>
                ) : (
                  occurrences.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.reservationDate, 'YYYY-MM-DD')}</td>
                      <td>{item.chargerOcppId}</td>
                      <td>{_.isNil(item.connectorId) ? 'All' : item.connectorId}</td>
                      <td>{formatWindow(item.bookedStartTime, item.bookedEndTime)}</td>
                      <td className="fr-blocked">{formatTime(item.blockStartTime)}</td>
                      <td>
                        <StatusPill status={item.status} />
                      </td>
                      <td>{formatWindow(item.actualStartTime, item.actualEndTime) || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <div className="fr-timeline">
            <b>History</b>
            {detail.history.length === 0 && <div className="fr-muted small mt-2">No history yet</div>}
            {detail.history.map((entry, index) => {
              const meta = HISTORY_META[entry.action] || { label: entry.action, dotClass: 'fr-dot--gray' };
              const text = historyText(entry);
              const scopeLabel = _.get(_.find(SCOPES, { value: entry.scopeOfChange }), 'label', '');
              const actor = _.get(entry, 'performedBy.name', entry.performedBy || '');
              return (
                <div className="fr-timeline__item" key={`${entry.action}-${entry.performedAt}-${index}`}>
                  <span className={`fr-dot ${meta.dotClass}`} />
                  <div>
                    <div>
                      <b>{meta.label}</b> {scopeLabel && <span className="fr-muted small">{scopeLabel}</span>}
                    </div>
                    {text && <div className="small">{text}</div>}
                    <div className="fr-timeline__meta">
                      {[actor, formatDateTime(entry.performedAt), entry.reason && `Reason: ${entry.reason}`].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </>
    );
  };

  return (
    <LayoutContainer>
      <AdminHeader title={t('header.fleetReservation', 'Fleet Reservation')} backTo="/fleet-reservations" />
      <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
        <div className="page-content-wrapper scrollable fr-page">
          <div className="fr-breadcrumb">
            <span className="fr-link" onClick={() => navigateTo('/fleet-reservations')}>
              Fleet Reservation
            </span>{' '}
            / {reservationCode}
          </div>
          {renderBody()}
        </div>
      </SkeletonTheme>

      <Modal show={!!modal} centered size="lg" onHide={() => setModal(null)}>
        <div className="customer-form-page__main fr-page">
          <Card>
            <div className="customer-form-header__block">
              <div className="customer-header-name">
                <h5>
                  {isCancel ? 'Cancel Reservation' : 'Reschedule Reservation'} — <span className="fr-code">{reservationCode}</span>
                </h5>
              </div>
              <div className="close___btn">
                <MdClose size={30} color="#BE210B" onClick={() => setModal(null)} />
              </div>
            </div>

            <div className="fr-modal-step">
              <div className="fr-modal-step__title">1. Apply changes to</div>
              <div className="d-flex flex-column gap-2">
                {SCOPES.map((option) => (
                  <RadioButton
                    key={option.value}
                    id={`fr-scope-${option.value}`}
                    name="scope"
                    checked={form.scope === option.value}
                    onChange={() => setScope(option.value)}
                  >
                    {option.label}
                  </RadioButton>
                ))}
              </div>
            </div>

            <div className="fr-modal-step">
              <div className="fr-modal-step__title">2. Target occurrence date</div>
              <input
                type="date"
                className="form-control fr-input"
                value={form.occurrenceDate}
                min={_.first(dates)}
                max={_.last(dates)}
                disabled={!needsDate}
                onChange={(e) => setFormField('occurrenceDate', e.target.value)}
              />
              {needsDate && (submitted || form.occurrenceDate) && formErrors.occurrenceDate && (
                <div className="text-danger small mt-1">{formErrors.occurrenceDate}</div>
              )}
              {!needsDate && <div className="fr-helper">Not needed for all future occurrences.</div>}
            </div>

            {isCancel ? (
              <div className="fr-modal-step">
                <div className="fr-modal-step__title">3. Reason for cancellation *</div>
                <input
                  className="form-control fr-input"
                  maxLength={200}
                  value={form.reason}
                  onChange={(e) => setFormField('reason', e.target.value)}
                />
                {submitted && formErrors.reason && <div className="text-danger small mt-1">{formErrors.reason}</div>}
              </div>
            ) : (
              <>
                <div className="fr-modal-step">
                  <div className="fr-modal-step__title">
                    3. New date <span className="fr-muted fw-normal">(optional — only for this occurrence)</span>
                  </div>
                  <input
                    type="date"
                    className="form-control fr-input"
                    value={form.newDate}
                    min={moment().format('YYYY-MM-DD')}
                    disabled={form.scope !== 'THIS_OCCURRENCE'}
                    onChange={(e) => setFormField('newDate', e.target.value)}
                  />
                </div>

                <div className="fr-modal-step">
                  <div className="fr-modal-step__title">4. New time window</div>
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="fr-label">From</div>
                      <input
                        type="time"
                        className="form-control fr-input"
                        value={form.timeFrom}
                        onChange={(e) => setFormField('timeFrom', e.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <div className="fr-label">To</div>
                      <input
                        type="time"
                        className="form-control fr-input"
                        value={form.timeTo}
                        onChange={(e) => setFormField('timeTo', e.target.value)}
                      />
                    </div>
                  </div>
                  {effectiveBlock && (
                    <div className="fr-helper">
                      Effective blocked window with {BUFFER_MINUTES}m buffer: <b className="fr-blocked">{effectiveBlock}</b>
                    </div>
                  )}
                  {submitted && formErrors.time && <div className="text-danger small mt-1">{formErrors.time}</div>}
                </div>

                <div className="fr-modal-step">
                  <div className="fr-modal-step__title">5. Reason for change *</div>
                  <input
                    className="form-control fr-input"
                    maxLength={200}
                    value={form.reason}
                    onChange={(e) => setFormField('reason', e.target.value)}
                  />
                  {submitted && formErrors.reason && <div className="text-danger small mt-1">{formErrors.reason}</div>}
                </div>
              </>
            )}

            <div className="d-flex flex-wrap justify-content-end gap-2 mt-4">
              <button type="button" className="btn fr-btn-outline" onClick={() => setModal(null)}>
                {isCancel ? 'Keep Reservation' : 'Cancel'}
              </button>
              <Button disabled={isSaving || !dates.length} onClick={handleSubmit}>
                {isCancel ? 'Confirm Cancellation' : 'Confirm Changes'}
              </Button>
            </div>
          </Card>
        </div>
      </Modal>
      <Footer />
    </LayoutContainer>
  );
};

export default FleetReservationDetail;
